import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Header } from '@components/Header';
import Add from '@components/icons/Add.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { usePermission } from '@providers/PermissionProvider/usePermission';

import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobResponseDto, RepositoriesService } from 'app/API';
import { useInvalidateQueriesOnChange } from 'app/hooks/useInvalidateQueriesOnChange';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { JobsTable } from 'app/pages/User/RepositoryPage/components/JobsTable';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import { EPermission } from 'app/types/enums/EPermission';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router';
import { v4 } from 'uuid';
import { createFetchRepositoryQueryFn } from './api/fetchRepository';
import { createFetchRepositoryJobsQueryFn } from './api/fetchRepositoryJobs';
import { MainBlock, TForm } from './components/blocks/MainBlock';
import { repositoryJobsListDefaults } from './constants/repositoryJobsListDefaults';
import { buildRepositoryForm } from './helpers/buildRepositoryForm';
import { Content } from './styles/Content';
import { RootWrapperDiv } from './styles/RootWrapperDiv';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

const EMPTY_JOBS: TPaginatedResponse<JobResponseDto> = { data: [], count: 0 };

export const RepositoryPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<TForm>({
    name: '',
    slug: '',
    notes: '',
    audioFiles: [],
    documentFiles: [],
    assignee: null,
  });

  const [slugIsAvailable, setSlugIsAvailable] = useState<boolean>(true);

  const params = useParams<{ repoSlug: string; orgSlug: string }>();
  const navigate = useNavigate();

  const { organization, workspace } = useAuthContext();

  const isNew = !params.repoSlug;
  const organizationSlug = params.orgSlug ?? '';
  const repositorySlug = params.repoSlug ?? '';

  const [associatedJobsDisclosureOpen, setAssociatedJobsDisclosureOpen] = useState(true);
  const [isAudioUploading, setIsAudioUploading] = useState(false);
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);

  const [repositoryId, setRepositoryId] = useState<string | null>(() => (isNew ? v4() : null));

  const { openModal, closeModal } = useModalContext();
  const [, canEditRepositories] = usePermission(EPermission.editRepositories);

  const repositoryQuery = useQuery({
    queryKey: repositoriesKeys.organizationDetail(organizationSlug, repositorySlug),
    queryFn: createFetchRepositoryQueryFn(call),
    enabled: organizationSlug !== '' && repositorySlug !== '',
    staleTime: Infinity,
    gcTime: 0,
  });
  const repository = repositoryQuery.data;
  useNotifyQueryError(repositoryQuery.error);

  useEffect(() => {
    if (!repository) {
      return;
    }
    setForm(buildRepositoryForm(repository));
    setRepositoryId(repository.id);
  }, [repository]);

  const jobsRepositoryId = isNew ? '' : repositoryId ?? '';
  const { page, pageSize, orderBy, orderType, search } = useListQueryParams({
    defaults: repositoryJobsListDefaults,
  });
  const jobsListParams = useMemo(
    () => ({ page, pageSize, orderBy, orderType, search }),
    [orderBy, orderType, page, pageSize, search],
  );
  const jobsQuery = useQuery({
    queryKey: jobsKeys.repositoryList(jobsRepositoryId, jobsListParams),
    queryFn: createFetchRepositoryJobsQueryFn(call),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[0].repositoryId === jobsRepositoryId ? previousData : undefined,
    enabled: jobsRepositoryId !== '' && !!canEditRepositories,
    refetchOnWindowFocus: false,
  });
  const jobs = jobsQuery.data ?? EMPTY_JOBS;
  useNotifyQueryError(jobsQuery.error);

  const repositoryJobsKey = useMemo(() => jobsKeys.listsByRepository(jobsRepositoryId), [jobsRepositoryId]);
  useInvalidateQueriesOnChange(workspace?.id, repositoryJobsKey);
  useInvalidateQueriesOnChange(organization?.id, repositoryJobsKey);

  const reloadJobs = () => queryClient.invalidateQueries({ queryKey: repositoryJobsKey });

  const saveMutation = useMutation({
    mutationFn: (id: string) =>
      isNew
        ? call(() =>
            RepositoriesService.create({
              requestBody: {
                id,
                name: form.name,
                slug: form.slug,
                notes: form.notes ?? '',
                organizationSlug,
                assignee: form.assignee?.id ?? null,
              },
            }),
          )
        : call(() =>
            RepositoriesService.update({
              repositoryId: id,
              requestBody: {
                name: form.name,
                slug: form.slug,
                notes: form.notes ?? '',
              },
            }),
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: repositoriesKeys.details() });
      navigate(routes.repositoriesList.make(params.orgSlug));
    },
    onError: notifyApiCallError,
  });

  const submit = (event: MouseEvent) => {
    event.preventDefault();

    if (!repositoryId || !form.name || !form.slug) {
      return;
    }

    saveMutation.mutate(repositoryId);
  };

  const isAllFilesReady = !isAudioUploading && !isDocumentUploading;

  const handleBulkSubmitModal = () => {
    if (!repositoryId || repositoryQuery.isLoading || !isAllFilesReady) {
      return;
    }

    openModal(ModalNamesEnum.RepositoryBulkJobCreation, {
      repositoryId,
      repository: form,
      organizationId: organization?.id,
      onSuccess: () => {
        closeModal();
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Create a new Case - AutoScript</title>
      </Helmet>
      <Header title={isNew ? 'Create Case' : form.name} backRoute={routes.repositoriesList.make(params.orgSlug)} />

      <LinearProgressLoader active={repositoryQuery.isFetching || jobsQuery.isFetching || saveMutation.isPending} />
      <Content>
        <form className="flex flex-col">
          <RootWrapperDiv>
            {repositoryId && params.orgSlug ? (
              <MainBlock
                repositoryId={repositoryId}
                organizationSlug={params.orgSlug}
                documentAttachments={form.documentFiles}
                audioAttachments={form.audioFiles}
                onDocumentUploadingChange={setIsDocumentUploading}
                onAudioUploadingChange={setIsAudioUploading}
                isFilesLoading={repositoryQuery.isLoading}
                form={form}
                setForm={setForm}
                slugIsAvailable={slugIsAvailable}
                setSlugIsAvailable={setSlugIsAvailable}
                isNew={isNew}
              />
            ) : null}
          </RootWrapperDiv>
          <div className="flex flex-col">
            <div className="grid place-items-end">
              <div className="w-36">
                <BoldButton
                  className="my-2 w-[140px]"
                  disabled={saveMutation.isPending || !isAllFilesReady || !slugIsAvailable}
                  onClick={submit}
                >
                  {isNew ? 'Create' : 'Update'}
                </BoldButton>
              </div>
            </div>
          </div>
        </form>
        <Restricted to={EPermission.editRepositories}>
          {!isNew ? (
            <DisclosureSection
              open={associatedJobsDisclosureOpen}
              setOpenState={setAssociatedJobsDisclosureOpen}
              loading={false}
              title={`Associated Jobs (${jobs.count || 0})`}
            >
              <div className="flex flex-col">
                <div className="grid place-items-end">
                  <div className="w-70 flex flex-row">
                    <div className="mr-2 text-center">
                      <OutlinedButton
                        className="w-[140px] gap-[5px]"
                        onClick={handleBulkSubmitModal}
                        disabled={repositoryQuery.isLoading || !repositoryId || !isAllFilesReady}
                        title={!isAllFilesReady ? 'Wait for all files to finish uploading.' : undefined}
                      >
                        Bulk Create Jobs
                      </OutlinedButton>
                    </div>
                    <div className="w-36">
                      <Button
                        className="w-[140px] gap-[5px]"
                        onClick={() =>
                          organization &&
                          workspace &&
                          navigate(`${routes.newJob.make(organization.slug, workspace.slug)}?repo=${form.slug}`)
                        }
                      >
                        <Add className="pl-1 mr-1" height="12px" />
                        Create New Job
                      </Button>
                    </div>
                  </div>
                </div>

                {repositoryId && !isNew ? <JobsTable jobs={jobs} reload={reloadJobs} /> : null}
              </div>
            </DisclosureSection>
          ) : null}
        </Restricted>
      </Content>
    </>
  );
};
